import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { AuthImage } from "@/components/AuthImage";
import { ItemThumb } from "@/components/ItemThumb";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Sparkles } from "lucide-react";

const LOOK = "https://images.unsplash.com/photo-1556906781-9a412961c28c?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";
const OCCASIONS = ["all", "casual", "work", "formal", "party", "sport"];

export default function Lookbook() {
  const [outfits, setOutfits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/outfits");
      setOutfits(data);
    } catch (e) {
      toast.error("Failed to load lookbook");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/outfits/${id}`);
      setOutfits((p) => p.filter((o) => o.id !== id));
      toast.success("Removed");
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const shown = filter === "all" ? outfits : outfits.filter((o) => o.occasion === filter);

  return (
    <div className="bg-bg0 min-h-screen">
      <Navbar />
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        <div className="overline mb-3 text-volt">Saved Fits</div>
        <h1 className="font-display uppercase text-6xl md:text-7xl font-bold tracking-tighter mb-10">Lookbook</h1>

        <div className="flex flex-wrap gap-6 border-b border-epborder pb-4 mb-10" data-testid="lookbook-filters">
          {OCCASIONS.map((o) => (
            <button
              key={o}
              onClick={() => setFilter(o)}
              data-testid={`lookbook-filter-${o}`}
              className={`overline ep-btn ${filter === o ? "text-volt" : "text-epmuted hover:text-white"}`}
            >
              {o}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-72 ep-loading" />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center border border-epborder" data-testid="lookbook-empty">
            <div className="md:col-span-5 relative">
              <img src={LOOK} alt="Lookbook" className="w-full h-[380px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
            <div className="md:col-span-6 md:col-start-7 p-8 md:p-4">
              <div className="overline mb-4 text-volt">No saved fits</div>
              <h2 className="font-display uppercase text-4xl font-bold tracking-tight mb-4">Curate your first look.</h2>
              <p className="text-epsub mb-8 leading-relaxed">
                Generate fits with the AI stylist and stash the ones that hit. They live here, filterable by occasion.
              </p>
              <Link
                to="/pair"
                data-testid="goto-pair"
                className="ep-btn inline-flex items-center gap-2 bg-volt text-black px-6 py-3 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover"
              >
                <Sparkles size={16} /> Generate Outfits
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="lookbook-grid">
            {shown.map((o) => (
              <div key={o.id} className="ep-card p-6" data-testid={`saved-outfit-${o.id}`}>
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <div className="overline mb-1 text-volt">{o.occasion}</div>
                    <h3 className="font-display uppercase text-2xl leading-tight tracking-tight">{o.name}</h3>
                  </div>
                  <div className="flex items-center gap-4">
                    {o.score > 0 && (
                      <div className="text-right">
                        <div className="font-display text-2xl font-bold text-volt leading-none">{o.score}</div>
                        <div className="overline">match</div>
                      </div>
                    )}
                    <button
                      onClick={() => remove(o.id)}
                      data-testid={`delete-outfit-${o.id}`}
                      className="ep-btn border border-white/25 p-2 hover:bg-epred hover:border-epred"
                    >
                      <Trash2 size={14} strokeWidth={1.75} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mb-4">
                  {o.items.map((it) => (
                    <div key={it.id} className="border border-epborder overflow-hidden">
                      <ItemThumb item={it} className="w-full aspect-[3/4] object-cover" />
                    </div>
                  ))}
                </div>
                {o.notes && <p className="text-sm text-epsub leading-relaxed">{o.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
